import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { BaselineService } from '../baseline.service';
import { SnackbarService } from 'src/app/core/services/snackbar/snackbar.service';
import { BaselineCategoryComponent } from '../baseline-category/baseline-category.component';
import { UIComponentsModule } from 'src/app/shared/ui-components/ui-components.module';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { DigitalizationComponent } from '../digitalization/digitalization.component';
import {
  baselineCategory,
  baselineConstant,
  baselineQuestion,
  Product,
  userBaselineEntry,
  UserFlightEntry,
  userProductEntry,
} from 'src/app/shared/models/interfaces';
import { catchError, forkJoin } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-overview',
  imports: [
    BaselineCategoryComponent,
    UIComponentsModule,
    MatRadioModule,
    MatIconModule,
    MatTooltipModule,
    FormsModule,
    DigitalizationComponent,
  ],
  templateUrl: './overview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './overview.component.scss',
})
export class OverviewComponent implements OnInit {
  private questions: baselineQuestion[] = [];
  baselineConstants: baselineConstant[] = [];
  userBaselineEntries: userBaselineEntry[] = [];
  categories: baselineCategory[] = [];
  selectedCategory: baselineCategory | null = null;


  digitalizationSelected: boolean = false;
  products: Product[] = [];
  userProductEntries: userProductEntry[] = [];
  userFlightEntries: UserFlightEntry[] = [];

  constructor(
    private baselineService: BaselineService,
    private snackbarService: SnackbarService
  ) {}

  ngOnInit() {
    this.loadAllData();
  }

  loadAllData() {
    forkJoin({
      questions: this.baselineService.getAllBaselineQuestionData(),
      baselineConstants: this.baselineService.getBaselineConstants(),
      categories: this.baselineService.getBaselineCategories(),
      baselineEntries: this.baselineService.getUserBaselineEntries(),
      productEntries: this.baselineService.getUserProductEntries(),
      products: this.baselineService.getDigitalProductData(),
      flightEntries: this.baselineService.getUserFlightEntries(),
    })
      .pipe(
        catchError((error) => {
          this.snackbarService.showErrorMessage('Failed to load data');
          throw error;
        })
      )
      .subscribe((data) => {
        this.questions = data.questions.filter(
          (q) => !q.hidden && q.baselineAnswers.length > 0
        );
        this.categories = data.categories;
        this.userBaselineEntries = data.baselineEntries as any[];
        this.userProductEntries = data.productEntries;
        this.products = data.products;
        this.userFlightEntries = data.flightEntries as unknown as UserFlightEntry[];
        this.baselineConstants = data.baselineConstants;
      });
  }

  getQuestionsForCategory(categoryId: number) {
    return this.questions.filter(
      (question) => question.categoryId === categoryId
    );
  }

  onCategoryClick(category: baselineCategory) {
    /*  Sets the selected category to the one that was clicked
        This will trigger the BaselineCategoryComponent to display the questions for that category
        The selected category is passed to the BaselineCategoryComponent */
    this.selectedCategory = category;
  }

  onBackToOverview() {
    /*  Sets the selected category to null so the component knows
        to display the overview again */
    this.selectedCategory = null;
    // Fetch the user flight entries again to ensure the state is up-to-date, because
    // the structure of the flight entries is different from the baseline questions
    // but also different from the digitalization entries.
    this.baselineService.getUserFlightEntries().subscribe({
      next: (entries) =>
        (this.userFlightEntries = entries as unknown as UserFlightEntry[]),
      error: () =>
        this.snackbarService.showErrorMessage(
          'Failed to refresh flight entries'
        ),
    });
  }

  getProgressForCategory(categoryId: number): number {
    /*  Checks how many questions can be found from the user entries and calculates
        the progress.
        Special case for mobility: here, the Flight entries are also included.  */

    const questionIds = this.getQuestionsForCategory(categoryId).map(
      (question) => question.id
    );
    const userEntryQuestionIds = this.userBaselineEntries.map(
      (entry) => entry.baselineQuestionId
    );
    const answeredQuestions = questionIds.filter((questionId) =>
      userEntryQuestionIds.includes(questionId)
    );

    // Special case for mobility category
    const flightEntriesAnswered =
      categoryId == 1 && this.hasAnsweredFlightEntries() ? 1 : 0;

    const totalQuestions = questionIds.length + (categoryId === 1 ? 1 : 0);

    return (
      ((answeredQuestions.length + flightEntriesAnswered) / totalQuestions) *
      100
    );
  }

  getAnsweredCount(categoryId: number): number {
    const questionIds = this.getQuestionsForCategory(categoryId).map(
      (q) => q.id
    );
    return (
      this.userBaselineEntries.filter((entry) =>
        questionIds.includes(entry.baselineQuestionId)
      ).length + (categoryId === 1 && this.hasAnsweredFlightEntries() ? 1 : 0)
    );
  }

  getTotalCount(categoryId: number): number {
    /*  Returns the total number of questions for a category 
    Special case for mobility: here, the Flight entries are also included.
    In this case, the users Answers are a combination of the baseline 
    questions and the Flights a user has added (Flights count as 1 question). */
    if (categoryId === 1) {
      return this.getQuestionsForCategory(categoryId).length + 1;
    }
    return this.getQuestionsForCategory(categoryId).length;
  }

  getCategoryButtonLabel(categoryId: number): string {
    const answered = this.getAnsweredCount(categoryId);
    const total = this.getTotalCount(categoryId);
    if (answered === 0) return 'Start questionnaire';
    if (answered === total) return 'Edit questionnaire';
    return 'Complete questionnaire';
  }

  getProgressColor(categoryId: number): string {
    return '#4caf50'; // TODO: replace placeholder for progress color
  }

  onBaselineAnswerChanged() {
    /*  With every change in the answers, after updating the database, 
        the entries are fetched again from the backend to directly mirror
        the state of the db */
    this.baselineService.getUserBaselineEntries().subscribe({
      next: (entries) => (this.userBaselineEntries = entries as any[]),
      error: () =>
        this.snackbarService.showErrorMessage('Failed to refresh entries'),
    });
  }

  onDigitalizationAnswerChanged() {
    /*  With every change in the answers, after updating the database, 
        the entries are fetched again from the backend to directly mirror
        the state of the db */
    this.baselineService.getUserProductEntries().subscribe({
      next: (entries) => (this.userProductEntries = entries),
      error: () =>
        this.snackbarService.showErrorMessage('Failed to refresh entries'),
    });
  }

  onDigitalizationSelectedToggle() {
    /*  Toggles the digitalization section to show or hide it */
    this.digitalizationSelected = !this.digitalizationSelected;
  }

  hasDigitalizationEntries(): boolean {
    return this.userProductEntries.length > 0;
  }

  getDigitalizationAnsweredCount(): number {
    return this.hasDigitalizationEntries() ? 1 : 0;
  }

  getDigitalizationProgress(): number {
    return this.hasDigitalizationEntries() ? 100 : 0;
  }

  hasAnsweredFlightEntries(): boolean {
    return this.userFlightEntries.length > 0;
  }
}
